import React from "react";
import {
  FaReact,
  FaNodeJs,
  FaPython,
  FaJsSquare,
  FaChartBar,
  FaCamera,
  FaRunning,
  FaSkiing,
  FaBiking,
  FaSnowflake,
  FaCogs,
  FaLayerGroup,
  FaChartLine,
  FaDatabase,
  FaDownload,
  FaLaptopCode,
  FaServer,
  FaChartArea,
  FaFileCode,
  FaTachometerAlt,
  FaTasks,
  FaShieldAlt,
  FaExpandArrowsAlt,
  FaCloudSun,
  FaMoneyBillWave,
  FaDumbbell,
  FaKey,
  FaMap,
  FaMountain,
  FaMusic,
  FaCodeBranch,
  FaRobot,
  FaComments,
} from "react-icons/fa";
import {
  SiPandas,
  SiTypescript,
  SiAmazon,
  SiTailwindcss,
  SiDocker,
  SiKubernetes,
  SiPostgresql,
  SiMysql,
  SiRedis,
  SiGraphql,
  SiGooglecloud,
  SiTerraform,
  SiAmazondynamodb,
  SiAmazonsqs,
  SiAwslambda,
  SiStrava,
} from "react-icons/si";
import { SlSpeedometer } from "react-icons/sl";

type IconRenderer = (opts?: { size?: number; className?: string }) => React.ReactElement;

const make =
  (
    Component: React.ComponentType<{ size?: number; className?: string }>,
    defaults?: { className?: string },
  ) =>
  (opts?: { size?: number; className?: string }) => (
    <Component
      size={opts?.size}
      className={`${defaults?.className || ""} ${opts?.className || ""}`.trim()}
    />
  );

// unified map combining previous maps (skills, interests, projects, courses)
const iconMap: Record<string, IconRenderer> = {
  // Skills / technologies
  react: make(FaReact, { className: "text-blue-400" }),
  nodejs: make(FaNodeJs, { className: "text-green-400" }),
  python: make(FaPython, { className: "text-yellow-400" }),
  pandas: make(SiPandas, { className: "text-purple-400" }),
  javascript: make(FaJsSquare, { className: "text-yellow-400" }),
  aws: make(SiAmazon, { className: "text-orange-400" }),
  typescript: make(SiTypescript, { className: "text-blue-400" }),
  tailwind: make(SiTailwindcss, { className: "text-sky-400" }),
  "data analytics": make(FaChartBar, { className: "text-blue-400" }),
  "data visualization": make(FaChartArea, { className: "text-purple-400" }),
  sql: make(FaDatabase, { className: "text-indigo-300" }),
  "performance engineering": make(SlSpeedometer, { className: "text-red-400" }),
  "performance tuning": make(FaTachometerAlt, { className: "text-red-400" }),
  "load testing": make(FaTachometerAlt, { className: "text-orange-400" }),
  "software architecture": make(FaCogs, { className: "text-gray-400" }),
  "object oriented design": make(FaLayerGroup, { className: "text-indigo-400" }),
  "infrastructure as code": make(SiTerraform, { className: "text-violet-400" }),
  microservices: make(FaLayerGroup, { className: "text-indigo-400" }),
  "api development": make(FaFileCode, { className: "text-green-400" }),
  "agile methodologies": make(FaTasks, { className: "text-yellow-400" }),
  resiliency: make(FaShieldAlt, { className: "text-teal-400" }),
  scalability: make(FaExpandArrowsAlt, { className: "text-indigo-300" }),
  dynamodb: make(SiAmazondynamodb, { className: "text-orange-400" }),
  "aws dynamodb": make(SiAmazondynamodb, { className: "text-orange-400" }),
  lambda: make(SiAwslambda, { className: "text-yellow-400" }),
  "aws lambda": make(SiAwslambda, { className: "text-yellow-400" }),
  sqs: make(SiAmazonsqs, { className: "text-indigo-400" }),
  "aws sqs": make(SiAmazonsqs, { className: "text-indigo-400" }),
  observability: make(FaChartLine, { className: "text-teal-400" }),

  // Interests
  // Music
  music: make(FaMusic, { className: "text-pink-400" }),
  diff: make(FaCodeBranch, { className: "text-green-400" }),

  photography: make(FaCamera, { className: "text-blue-400" }),
  running: make(FaRunning, { className: "text-blue-400" }),
  cycling: make(FaBiking, { className: "text-blue-400" }),
  skiing: make(FaSkiing, { className: "text-blue-400" }),

  // Projects / apps
  "activityviz.com": make(FaRunning, { className: "text-green-400" }),
  activityviz: make(FaRunning, { className: "text-green-400" }),
  "most said": make(FaComments, { className: "text-cyan-400" }),
  mostsaid: make(FaComments, { className: "text-cyan-400" }),
  snowstats: make(FaSnowflake, { className: "text-blue-300" }),
  finapp: make(FaChartBar, { className: "text-yellow-400" }),
  "(all) down hill from here": make(FaMountain, { className: "text-slate-400" }),
  "downhill from here": make(FaMountain, { className: "text-slate-400" }),
  // additional project/topic icons
  strava: make(SiStrava, { className: "text-orange-400" }),
  weather: make(FaCloudSun, { className: "text-sky-400" }),
  finance: make(FaMoneyBillWave, { className: "text-green-400" }),
  fitness: make(FaDumbbell, { className: "text-pink-400" }),
  oauth2: make(FaKey, { className: "text-yellow-300" }),
  oauth: make(FaKey, { className: "text-yellow-300" }),
  mapping: make(FaMap, { className: "text-emerald-400" }),
  "terrain analysis": make(FaMountain, { className: "text-slate-400" }),

  // Course icons
  databases: make(FaDatabase, { className: "text-indigo-300" }),
  "software engineering": make(FaLaptopCode, { className: "text-green-400" }),
  algorithms: make(FaChartBar, { className: "text-yellow-400" }),
  "operating systems": make(FaServer, { className: "text-gray-400" }),

  // Infrastructure / additional tech (suggestions)
  docker: make(SiDocker, { className: "text-blue-300" }),
  kubernetes: make(SiKubernetes, { className: "text-indigo-300" }),
  postgresql: make(SiPostgresql, { className: "text-blue-600" }),
  mysql: make(SiMysql, { className: "text-blue-600" }),
  redis: make(SiRedis, { className: "text-red-400" }),
  graphql: make(SiGraphql, { className: "text-pink-400" }),
  gcp: make(SiGooglecloud, { className: "text-blue-400" }),
  "systems administration": make(FaServer, { className: "text-gray-400" }),
  "reverse proxy": make(FaCogs, { className: "text-gray-400" }),
  "fuzzy matching": make(FaChartBar, { className: "text-purple-400" }),
  homelab: make(FaServer, { className: "text-gray-400" }),
  langgraph: make(FaLayerGroup, { className: "text-indigo-400" }),
  llm: make(FaRobot, { className: "text-violet-400" }),
  skills: make(FaCogs, { className: "text-gray-400" }),
  tools: make(FaCogs, { className: "text-gray-400" }),
  agent: make(FaRobot, { className: "text-cyan-400" }),
  calendar: make(FaTasks, { className: "text-yellow-400" }),
  planning: make(FaTasks, { className: "text-yellow-400" }),
};

export const suggestedIconMappings: { [key: string]: string } = {
  Docker: "SiDocker",
  Kubernetes: "SiKubernetes",
  Postgres: "SiPostgresql",
  MySQL: "SiMysql",
  Redis: "SiRedis",
  GraphQL: "SiGraphql",
  "AWS Lambda": "SiAmazonaws",
  GCP: "SiGooglecloud",
};

export const getIcon = (
  name?: string,
  opts?: { size?: number; className?: string },
): React.ReactElement | undefined => {
  if (!name) return undefined;
  const key = name.toLowerCase();
  // direct match
  const direct = iconMap[key] as IconRenderer | undefined;
  if (direct) return direct(opts);
  // try partial match
  const found = Object.keys(iconMap).find((k) => k.includes(key) || key.includes(k));
  if (found) {
    const renderer = iconMap[found] as IconRenderer | undefined;
    return renderer?.(opts);
  }
  return undefined;
};

export const getDownloadIcon = (opts?: { size?: number; className?: string }) =>
  make(FaDownload, {})(opts);
